import { LazyQueryExecFunction } from "@apollo/client";
import { CheckCircle, CloudUpload, Publish } from "@mui/icons-material";
import { CircularProgress, Grid, TextField, Tooltip } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import clsx from "clsx";
import Link from "components/base/Link";
import KeyAndNameInputs from "components/form/KeyAndNameInputs";
import TagsInput from "components/form/TagsInput";
import { Select } from "components/select";
import {
  FormikErrors,
  FormikHandlers,
  FormikTouched,
  FormikValues
} from "formik";
import React, { Dispatch, FC, useEffect } from "react";
import { DropzoneArea } from "react-mui-dropzone";
import { uploadTemporaryObject } from "utils/storage";
import { SetFieldValue } from "utils/typescript";
import {
  InputTableOptions,
  InputTableOptionsVariables
} from "./hooks/schema/InputTableOptions";
import { Action, State } from "./hooks/usePublishFormReducer";
import useStyles from "./hooks/usePublishFormStyles";
import { extractInputSchemaTableNames } from "./hooks/useQueryInputTableOptions";

function readDataUrl(file?: Blob) {
  return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    if (!file) return reject(new Error("No file provided"));
    const reader = new FileReader();
    reader.onabort = () => reject(new Error("File read was aborted"));
    reader.onerror = () =>
      reject(reader.error || new Error("Unable to read file"));
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

interface PublishFormProps {
  solutionKey: string;
  state: State;
  dispatch: Dispatch<Action>;
  getTableOptions?: LazyQueryExecFunction<
    InputTableOptions,
    InputTableOptionsVariables
  >;
  updateUseCase: boolean;
  errors: FormikErrors<FormikValues>;
  touched: FormikTouched<FormikValues>;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => void;
  handleBlur: FormikHandlers["handleBlur"];
  isValid?: boolean;
  setFieldValue: SetFieldValue<keyof State>;
  values: FormikValues;
  version?: string;
  setFieldTouched: (
    value: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  validateForm: (
    values?: Partial<State>
  ) => Promise<FormikErrors<FormikValues>>;
}

const PublishForm: FC<PublishFormProps> = ({
  solutionKey,
  getTableOptions,
  state,
  values: { name, key, calculationRuleAddress },
  dispatch,
  updateUseCase,
  errors,
  touched,
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  version,
  validateForm
}) => {
  const classes = useStyles();
  const {
    loadImage,
    loadRule,
    loadTransactionTableNames,
    transactionTableNames,
    tags,
    image,
    autoGeneratedKey,
    dropzoneKey
  } = state;
  useEffect(() => {
    if (updateUseCase) {
      validateForm();
    }
  }, [updateUseCase, validateForm]);

  const IconComponent = (loading?: boolean) =>
    loading ? (
      <div className={classes.iconComponent}>
        <CircularProgress className={classes.fileUploadProgress} />
        <Publish className={classes.uploadUncompletedIcon} />
      </div>
    ) : (
      <Publish className={classes.uploadUncompletedIcon} />
    );
  const CalculationRuleIconComponent = () => IconComponent(loadRule);
  const UseCaseImageIconComponent = () => IconComponent(loadImage);

  const fetchTransactionTableNames = async (
    calculationRuleAddress: string,
    useCaseKey: string
  ) => {
    if (!getTableOptions) return;
    if (!useCaseKey) return;
    if (!calculationRuleAddress) return;

    dispatch({ type: "LOAD_TRANSACTION_TABLE_NAMES", value: true });
    try {
      const { data } = await getTableOptions({
        variables: {
          solutionKey,
          useCaseKey,
          calculationRuleAddress
        }
      });

      const names = extractInputSchemaTableNames(data);
      dispatch({ type: "SET_TRANSACTION_TABLE_NAMES", value: names });
      if (state.transactionTable && !names.includes(state.transactionTable)) {
        dispatch({ type: "SET_TRANSACTION_TABLE", value: null });
        setFieldValue("transactionTable", null);
      }
    } finally {
      dispatch({ type: "LOAD_TRANSACTION_TABLE_NAMES", value: false });
    }
  };
  return (
    <form
      onSubmit={() => {
        handleSubmit();
      }}
      className={classes.form}
      noValidate
    >
      <Grid container direction="column">
        <KeyAndNameInputs
          value={state.key}
          name={state.name}
          autoGeneratedKey={autoGeneratedKey}
          onKeyChange={value => {
            dispatch({ type: "SET_KEY", value });
            fetchTransactionTableNames(calculationRuleAddress, value);
          }}
          onNameChange={value => dispatch({ type: "SET_NAME", value })}
          onKeyReset={() => dispatch({ type: "GENERATE_KEY" })}
          errors={errors}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          featureName="web service"
          updateMode={updateUseCase}
          touched={touched}
          handleBlur={handleBlur}
        />
        {version && !updateUseCase ? (
          <Link
            to={`/create-use-case-version/${key}`}
            className={classes.link}
            data-testid="version-exists"
          >
            Publish new version
            <CloudUpload style={{ marginLeft: 8 }} />
          </Link>
        ) : null}
        <Tooltip
          title="The description may be used to describe the feature or to take notes for it"
          placement="top-start"
        >
          <TextField
            id="outlined-description"
            label="Description"
            name="description"
            inputProps={{ maxLength: 1000 }}
            multiline={true}
            minRows={2}
            maxRows={10}
            value={state.description}
            onChange={e => {
              e.persist();
              setFieldTouched("description", true, false);
              const value = e.target.value;
              dispatch({ type: "SET_DESCRIPTION", value });
              handleChange(e);
            }}
            margin="normal"
            variant="outlined"
            InputLabelProps={{
              className: classes.label
            }}
            InputProps={{
              classes: {
                notchedOutline: classes.notchedOutline
              }
            }}
          />
        </Tooltip>
        <Grid container wrap="nowrap" justifyContent="space-between">
          <Tooltip
            title="Calculation-Rule files can be created within the SCS and contain logic that transforms tabular input to tabular output data"
            placement="top-start"
          >
            <div className={clsx(classes.relative, classes.adjoin)}>
              <DropzoneArea
                key={`dropzone file ${dropzoneKey}`}
                showPreviewsInDropzone={true}
                useChipsForPreview
                Icon={
                  calculationRuleAddress
                    ? CheckCircle
                    : CalculationRuleIconComponent
                }
                onDrop={async files => {
                  const file = files[0];
                  dispatch({ type: "LOAD_RULE", value: true });
                  if (file) {
                    const calculationRuleAddress = await uploadTemporaryObject(
                      file,
                      name,
                      solutionKey
                    );

                    dispatch({
                      type: "DROP_RULE",
                      value: calculationRuleAddress
                    });
                    setFieldValue(
                      "calculationRuleAddress",
                      calculationRuleAddress
                    );
                    dispatch({ type: "LOAD_RULE", value: false });

                    await fetchTransactionTableNames(
                      calculationRuleAddress,
                      key
                    );
                  }
                }}
                onDelete={() => {
                  dispatch({ type: "DROP_RULE", value: null });
                  dispatch({ type: "SET_TRANSACTION_TABLE_NAMES", value: [] });
                  setFieldValue("transactionTableNames", []);
                  setFieldValue("calculationRuleAddress", null);
                  dispatch({ type: "LOAD_RULE", value: false });
                }}
                filesLimit={1}
                acceptedFiles={[".dtf", ".hive", ".zip"]}
                dropzoneText={"Drag or drop here or browse calculation rule *"}
                classes={{
                  root: clsx(
                    classes.dropzoneClass,
                    classes.dropzoneHack,
                    calculationRuleAddress
                      ? classes.uploadCompleted
                      : classes.uploadUncompleted,
                    errors.calculationRuleAddress
                      ? classes.dropzoneErrorBorder
                      : classes.dropzoneBorderColor
                  ),
                  icon: calculationRuleAddress
                    ? classes.uploadCompletedIcon
                    : classes.uploadUncompletedIcon,
                  text: classes.text,
                  textContainer: classes.textContainer
                }}
                previewGridClasses={{
                  container: classes.gridContainer
                }}
                previewChipProps={{
                  classes: {
                    root: classes.maxWidth
                  }
                }}
                maxFileSize={250 * 1024 * 1024} // 250 MB
              />
            </div>
          </Tooltip>
          <Tooltip
            title="The icon shown in the calculation rule"
            placement="top-start"
          >
            <div className={clsx(classes.relative, classes.adjoin)}>
              <DropzoneArea
                key={`dropzone icon ${dropzoneKey}`}
                showPreviewsInDropzone={true}
                useChipsForPreview
                Icon={image ? CheckCircle : UseCaseImageIconComponent}
                onDrop={async files => {
                  const file = files[0];
                  dispatch({ type: "LOAD_ICON", value: true });
                  const value = await readDataUrl(file);
                  dispatch({ type: "DROP_ICON", value });
                }}
                onDelete={() => dispatch({ type: "DROP_ICON", value: null })}
                filesLimit={1}
                acceptedFiles={["image/*"]}
                dropzoneText={"Drag or drop here or browse calculation icon"}
                initialFiles={image ? [image] : []}
                classes={{
                  root: clsx(
                    classes.dropzoneClass,
                    classes.dropzoneBorderColor,
                    !image ? classes.uploadUncompleted : classes.uploadCompleted
                  ),
                  icon: image
                    ? classes.uploadCompletedIcon
                    : classes.uploadUncompletedIcon,
                  text: classes.text,
                  textContainer: classes.textContainer
                }}
                previewGridClasses={{
                  container: classes.gridContainer
                }}
                previewChipProps={{
                  classes: {
                    root: classes.maxWidth
                  }
                }}
              />
            </div>
          </Tooltip>
        </Grid>
        <Tooltip
          title="The input table to be used to calculate and store the number of transactions made for calls on the web service, with each row representing a single transaction. These transactions are priced and part of the service products available for purchase. "
          placement="top-start"
        >
          <div className={classes.dropdown}>
            <Select<string>
              label="Transaction table *"
              loading
              options={transactionTableNames || []}
              value={state.transactionTable || null}
              disabled={
                !Boolean(loadTransactionTableNames) &&
                !transactionTableNames?.length
              }
              onChange={(e, value) => {
                dispatch({ type: "SET_TRANSACTION_TABLE", value });
                setFieldValue("transactionTable", value);
              }}
              onBlur={handleBlur}
              error={Boolean(
                touched.transactionTable && errors.transactionTable
              )}
              helperText={
                touched.transactionTable &&
                typeof errors.transactionTable === "string"
                  ? errors.transactionTable
                  : ""
              }
              id="transactionTable"
            />
          </div>
        </Tooltip>
        <Tooltip
          title="A list of tags which help to categorize, identify or bookmark a web service"
          placement="top-start"
        >
          <div>
            <TagsInput tags={tags} dispatch={dispatch} />
          </div>
        </Tooltip>
      </Grid>
    </form>
  );
};

export const PrimaryButton: FC<ButtonProps> = props => {
  const classes = useStyles();
  return (
    <Button
      {...props}
      variant="contained"
      color="primary"
      className={clsx(classes.button, classes.buttonPrimary)}
    />
  );
};

export const SecondaryButton: FC<ButtonProps> = props => {
  const classes = useStyles();
  return (
    <Button
      {...props}
      variant="contained"
      className={clsx(classes.button, classes.buttonSecondary)}
    />
  );
};

export default PublishForm;
